<template>
  <content-page :class="$setClasses"
                :loading="isLoading">
    <template v-slot:header>
      <wrapper-navbar class="--back">
        <el-button @click="previousStep">
          <font-awesome-icon :icon="['fas', 'angle-left']" class="fa-lg" />
          <span class="u-margin-left-small u-font-weight-bold">{{ $t('comparator.back') }}</span>
        </el-button>
      </wrapper-navbar>
    </template>
    <h2 class="u-margin-left-small u-margin-vt-small u-color-white">
      {{ $t('mover.title', { name: mover && mover.customName }) }}</h2>
    <div v-if="mover"
         :class="$vClass('body')">
      <el-card v-if="mover" :class="$vClass('content')">
        <div :class="$vClass('content__wrapper')">
          <carousel v-if="mover.photos && mover.photos.length"
                    :class="$vClass('carousel')"
                    :height="heightCarousel"
                    :photos="mover.photos"
                    indicator-position="none"/>
          <mover-description :mover="mover"
                            class="no-padding"
                            display-description
                            hide-no-rating/>
          <div v-if="mover.ranking_details"
              class="u-margin-top-default">
            <h3 :class="$vClass('subtitle')">{{ $t('mover.rating') }}</h3>
            <offer-rates :rates="mover.ranking_details"></offer-rates>
          </div>
          <div class="u-margin-top-default">
            <h3 :class="$vClass('subtitle')">{{ $t('mover.info') }}</h3>
            <p v-html="$t('mover.infoText', moverData)"></p>
          </div>
          <div v-if="mover.address" class="u-margin-top-default">
            <h3 :class="$vClass('subtitle')">{{ $t('mover.localisation') }}</h3>
            <div :class="$vClass('address')">
              <font-awesome-icon
                :icon="['fas', 'location-dot']"
                :class="$vClass('address__icon')" />
              <div :class="$vClass('address__text')">
                <b>{{ mover.name }}</b><br>
                <span>{{ mover.address.city }} ({{ mover.address.postal.substring(0, 2) }})</span>
              </div>
            </div>
            <nxt-maps
              :markers="[{address: mover.address, title: mover.name}]"
              :zoom="8"
              :disable-default-ui="true"
              :class="$vClass('section__map')"></nxt-maps>
          </div>
          <!-- TODO get list articles -->
          <div v-if="mover.articles"
              :class="$vClass('grid')">
            <card-flex v-for="[item, index] of mover.articles"
                      :image="item.image"
                      :key="`article-${index}`"
                      :mobile-wrapper-column="true"
                      :title="item.title"
                      is-column>
              <template v-slot:after>
                <div v-html="item.description"></div>
              </template>
            </card-flex>
          </div>
        </div>
        <sticky-card-footer :class="[$vClass('footer')]" v-if="showFooter">
          <el-button @click="nextStep"
                 :disabled="isLoading"
                 :loading="isLoading"
                 class="el-button--success">
            {{ nextButtonText }}
            <font-awesome-icon :icon="['fas', 'angle-right']" class="fa-lg" />
          </el-button>
        </sticky-card-footer>
      </el-card>
    </div>
  </content-page>
</template>

<script>
import { mapGetters } from 'vuex';
import ContentPage from '../components/layout/ContentPage.vue';
import CardFlex from '../utils/components/CardFlex.vue';
import MoverDescription from '../components/offer/fragments/MoverDescription.vue';
import ConfigLoader from '../utils/components/ConfigLoader.vue';
import Carousel from '../utils/components/Carousel.vue';
import OfferRates from '../components/offer/fragments/OfferRates.vue';
import breakpoints from '../assets/scss/breakpoints-export.scss';
import WrapperNavbar from '../utils/components/WrapperNavbar.vue';
import StickyCardFooter from '../utils/components/StickyCardFooter.vue';
import offers from '../utils/helpers/offers';

export default {
  name: 'mover',
  nameClassStyle: 'mover',
  extends: ConfigLoader,
  components: {
    WrapperNavbar,
    CardFlex,
    Carousel,
    ContentPage,
    MoverDescription,
    OfferRates,
    StickyCardFooter,
  },
  data() {
    return {
      isDisplayMoverPhotos: true,
      activeCollapses: undefined,
      heightCarousel: '150px',
      forceGetOffer: true,
      forceReloadOnCreate: false,
    };
  },
  computed: {
    ...mapGetters({
      currentOffer: 'configStore/offer',
    }),
    mover() {
      return this.currentOffer && this.currentOffer.mover;
    },
    moverData() {
      return {
        yearEntered: new Date(this.mover.date_entered).getFullYear(),
        licencesCount: (this.mover.nb_licence_pl ?? 0) + (this.mover.nb_licence_vl ?? 0)
          || this.$t('mover.infoNull'),
        ape_code: this.mover.ape_code || this.$t('mover.infoNull'),
        siret: this.mover.siret || this.$t('mover.infoNull'),
        name: this.mover.name,
      };
    },
    nextButtonText() {
      return this.$t('mover.button');
    },
    showFooter() {
      if (this.isInitialState) {
        return this.isInitialStatus;
      }
      return !this.isInitialStatus
        && this.currentOffer
        && !offers.isFinishedStatus(this.currentOffer);
    },
  },
  async created() {
    if (breakpoints.tablet <= window.innerWidth) {
      this.heightCarousel = '350px';
    }
  },
  methods: {
    previousStep() {
      this.$router.push({
        name: 'comparator',
      });
    },
    nextStep() {
      this.$router.push({
        name: 'booking',
        params: { id: this.currentOffer && this.currentOffer.id },
        hash: '#resume',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.content-page__title) {
  margin: 0;
}

.mover {

  &__body {
    position: relative;
  }

  &__content {
    border: 0;
    overflow: initial;
  }

  &__subtitle {
    margin-block: 0;
    margin-bottom: 11px;
  }

  p {
    margin-block: 0;
  }

  &__address {
    display: flex;
    margin: 10px 0;
    align-items: center;
    &__icon {
      font-size: 20px;
      margin: 0 10px;
    }
  }

  &__section__map {
    margin: 20px -20px -20px;
    border-radius: 0;
  }

  :deep(.map) {
    @include breakpoint(desktop) {
      height: 260px;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__carousel {
    margin: -20px -20px 20px -20px;
  }

  &__carousel, :deep(.el-carousel__container),
  :deep(.el-carousel__item), :deep(.el-carousel__item) img {

    @include breakpoint(tablet) {
      border-radius: var(--nxt-border-radius-base) var(--nxt-border-radius-base) 0 0;
    }
  }

  &__grid {
    margin-top: var(--nxt-space-large);
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 20px;

    @include breakpoint(mobile) {
      grid-template-columns: 1fr 1fr;
    }

    @include breakpoint(tablet) {
      grid-template-columns: 1fr 1fr 1fr;
    }

    :deep(.card-flex__wrapper) {
      align-items: start;
    }

    :deep(.card-flex__content) {
      text-align: start;
      margin-left: 20px;
    }
  }
}
</style>
